import React, { useRef, useState } from "react";
import ReactModal from "react-modal";
import classNames from "classnames";
import ErrorRender from "./ErrorRender";
import { ErrorBoundary } from "@sentry/react";

const modalAppElement = typeof document !== "undefined" ? document.getElementById("root") : null;
if (modalAppElement) ReactModal.setAppElement(modalAppElement);

// Mobile breakpoint — keep in sync with useViewport().isMobile (< 950px).
const MOBILE_MAX = 949;

function Modal({
  children,
  title,
  size,
  show,
  close,
  action,
  className,
  classNameWrapper,
  hideClose,
  hideTitle,
  closeLabel = "Close",
  // When true: on mobile (<= 949px) the modal renders a top grabber band
  // and the user can drag downward to dismiss. Past 120px OR a fast
  // downward flick → close. Below threshold → snap back. iOS-sheet idiom.
  // Default false so existing modals keep their current behavior.
  swipeDownToClose = false,
  // Opt-in Escape-to-dismiss for informational modals (no form state to
  // lose). Default false — form-bearing modals keep the global no-Esc rule.
  closeOnEsc = false,
}) {
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);

  const handlePointerDown = (e) => {
    // Mobile only — desktop modals stay click-only.
    if (typeof window !== "undefined" && window.innerWidth > MOBILE_MAX) return;
    // Primary pointer only; ignore taps inside interactive descendants so
    // the user can interact with form fields placed near the grabber.
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest("button, a, [role='button'], select, input, textarea")) return;
    dragStartRef.current = { y: e.clientY, time: Date.now() };
    setIsDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragStartRef.current) return;
    // Only track downward motion. Negative deltas clamp to 0 so the user
    // can't drag the modal off the top of the viewport.
    const delta = Math.max(0, e.clientY - dragStartRef.current.y);
    setDragOffsetY(delta);
  };

  const handlePointerUp = (e) => {
    if (!dragStartRef.current) return;
    const delta = e.clientY - dragStartRef.current.y;
    const elapsed = Date.now() - dragStartRef.current.time;
    const velocity = delta / Math.max(elapsed, 1); // px per ms
    const shouldClose = delta > 120 || velocity > 0.6;
    dragStartRef.current = null;
    setIsDragging(false);
    if (shouldClose) {
      close?.();
    } else {
      setDragOffsetY(0);
    }
  };

  // While dragging: kill the transition so the modal tracks the finger 1:1.
  // On release (delta below threshold): re-enable transition for a smooth
  // snap back to 0. When swipeDownToClose is off, no inline style applied.
  const dragStyle = swipeDownToClose
    ? {
        content: {
          transform: `translateY(${dragOffsetY}px)`,
          transition: isDragging ? "none" : "transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        },
      }
    : undefined;

  return (
    <ReactModal
      className={classNames("modal", size && size, show && "show", className, isDragging && "is-dragging")}
      overlayClassName={"modal-wrapper " + classNameWrapper}
      isOpen={show}
      contentLabel={title || "Dialog"}
      onRequestClose={close}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={closeOnEsc}
      style={dragStyle}
    >
      {swipeDownToClose && (
        <div
          className="modal-swipe-band"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          aria-hidden="true"
        >
          <div className="modal-grabber" />
        </div>
      )}

      {!hideClose && (
        <button type="button" className="icon-btn close-modal" aria-label={closeLabel} disabled={!close} onClick={close}>
          <i className="notranslate material-icons">close</i>
        </button>
      )}

      {!hideTitle && title && (
        <div className="modal-title">
          <h2>{title}</h2>
        </div>
      )}

      <ErrorBoundary fallback={<ErrorRender></ErrorRender>}>
        <>
          {children && <div className="modal-content">{children}</div>}

          {action && <div className="modal-actions">{action}</div>}
        </>
      </ErrorBoundary>
    </ReactModal>
  );
}

export default Modal;
